import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Product } from '../types'
import { localDb, formatCurrency } from '../lib/db'
import { useCart } from '../contexts/CartContext'
import { useToast } from '../contexts/ToastContext'
import './ProductDetail.css'

export default function ProductDetail() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const [product, setProduct] = useState<Product | null>(null)
    const [quantity, setQuantity] = useState(1)
    const [isLoading, setIsLoading] = useState(true)
    const [showAiImage, setShowAiImage] = useState(false)
    const { addItem } = useCart()
    const { showToast } = useToast()

    useEffect(() => {
        if (id) {
            const found = localDb.products.getById(id)
            setProduct(found || null)
            setIsLoading(false)
        }
    }, [id])

    const handleAddToCart = () => {
        if (product) {
            addItem(product, quantity)
            showToast(`${quantity}x ${product.name} added to cart`, 'success')
        }
    }

    const handleBuyNow = () => {
        if (product) {
            addItem(product, quantity)
            navigate('/checkout')
        }
    }

    const getDiscountPercentage = (mrp: number, smPrice: number) => {
        return Math.round(((mrp - smPrice) / mrp) * 100)
    }

    if (isLoading) {
        return (
            <div className="container">
                <div className="product-detail-skeleton">
                    <div className="skeleton" style={{ aspectRatio: '1', borderRadius: '16px' }}></div>
                    <div className="product-detail-info-skeleton">
                        <div className="skeleton" style={{ height: '32px', width: '80%' }}></div>
                        <div className="skeleton" style={{ height: '24px', width: '40%', marginTop: '16px' }}></div>
                        <div className="skeleton" style={{ height: '100px', marginTop: '24px' }}></div>
                    </div>
                </div>
            </div>
        )
    }

    if (!product) {
        return (
            <div className="container">
                <div className="not-found">
                    <span className="not-found-icon">🔍</span>
                    <h2>Product not found</h2>
                    <p>The product you're looking for doesn't exist or has been removed.</p>
                    <Link to="/" className="btn btn-primary">Back to Shop</Link>
                </div>
            </div>
        )
    }

    return (
        <div className="container">
            {/* Breadcrumb */}
            <nav className="breadcrumb">
                <Link to="/">Shop</Link>
                <span>/</span>
                <Link to={`/?category=${product.category}`}>{product.category}</Link>
                <span>/</span>
                <span>{product.name}</span>
            </nav>

            <div className="product-detail">
                {/* Image Gallery */}
                <div className="product-gallery">
                    <div className="product-main-image">
                        {product.ai_model_image_url && showAiImage ? (
                            <img src={product.ai_model_image_url} alt={`${product.name} on model`} />
                        ) : product.image_url ? (
                            <img src={product.image_url} alt={product.name} />
                        ) : (
                            <div className="product-placeholder">👗</div>
                        )}

                        {getDiscountPercentage(product.mrp, product.sm_price) > 0 && (
                            <span className="discount-badge-large">
                                {getDiscountPercentage(product.mrp, product.sm_price)}% OFF
                            </span>
                        )}
                    </div>

                    {/* Image Toggle */}
                    {product.ai_model_image_url && (
                        <div className="image-toggle">
                            <button
                                className={`image-toggle-btn ${!showAiImage ? 'active' : ''}`}
                                onClick={() => setShowAiImage(false)}
                            >
                                Product Image
                            </button>
                            <button
                                className={`image-toggle-btn ${showAiImage ? 'active' : ''}`}
                                onClick={() => setShowAiImage(true)}
                            >
                                ✨ AI Model View
                            </button>
                        </div>
                    )}
                </div>

                {/* Product Info */}
                <div className="product-info">
                    <span className="product-category-badge">{product.category}</span>

                    <h1 className="product-title">{product.name}</h1>

                    {/* Pricing */}
                    <div className="product-pricing">
                        <div className="pricing-row">
                            <span className="label">MRP:</span>
                            <span className="price-mrp-detail">{formatCurrency(product.mrp)}</span>
                        </div>
                        <div className="pricing-row highlight">
                            <span className="label">SM Price:</span>
                            <span className="price-sm">{formatCurrency(product.sm_price)}</span>
                            <span className="savings-badge">
                                Save {formatCurrency(product.mrp - product.sm_price)}
                            </span>
                        </div>
                    </div>

                    {/* Description */}
                    {product.description && (
                        <div className="product-description">
                            <h3>Description</h3>
                            <p>{product.description}</p>
                        </div>
                    )}

                    {/* Stock Status */}
                    <div className="stock-status">
                        {product.stock > 0 ? (
                            <>
                                <span className="stock-dot in-stock"></span>
                                <span className="stock-text">
                                    {product.stock <= 5 ? `Only ${product.stock} left!` : 'In Stock'}
                                </span>
                            </>
                        ) : (
                            <>
                                <span className="stock-dot out-of-stock"></span>
                                <span className="stock-text">Out of Stock</span>
                            </>
                        )}
                    </div>

                    {/* Quantity Selector */}
                    {product.stock > 0 && (
                        <div className="quantity-selector">
                            <span className="quantity-label">Quantity:</span>
                            <div className="quantity-controls">
                                <button
                                    className="quantity-btn"
                                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                                    disabled={quantity <= 1}
                                >
                                    −
                                </button>
                                <span className="quantity-value">{quantity}</span>
                                <button
                                    className="quantity-btn"
                                    onClick={() => setQuantity(q => Math.min(product.stock, q + 1))}
                                    disabled={quantity >= product.stock}
                                >
                                    +
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="product-actions">
                        <button
                            className="btn btn-lg btn-secondary"
                            onClick={handleAddToCart}
                            disabled={product.stock === 0}
                        >
                            Add to Cart
                        </button>
                        <button
                            className="btn btn-lg btn-primary"
                            onClick={handleBuyNow}
                            disabled={product.stock === 0}
                        >
                            Buy Now
                        </button>
                    </div>

                    {/* S-Coins Info */}
                    <div className="scoins-info">
                        <span className="scoins-icon">🪙</span>
                        <span>
                            Earn <strong>{Math.floor(product.sm_price * quantity / 100) * 10} S-Coins</strong> on this purchase
                        </span>
                    </div>

                    {/* Features */}
                    <div className="product-features">
                        <div className="feature-item">
                            <span className="feature-icon">🏪</span>
                            <div>
                                <strong>Pay & Pickup</strong>
                                <p>Order online, pickup at store</p>
                            </div>
                        </div>
                        <div className="feature-item">
                            <span className="feature-icon">💳</span>
                            <div>
                                <strong>UPI Payment</strong>
                                <p>Secure QR code payment</p>
                            </div>
                        </div>
                        <div className="feature-item">
                            <span className="feature-icon">🪙</span>
                            <div>
                                <strong>Earn S-Coins</strong>
                                <p>10 coins per ₹100 spent</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
